import React, { useEffect, useRef, useState } from 'react';
import { History, Loader2, Lock } from 'lucide-react';
import { uc } from './unifiedContactsApi';
import { formatDateTime } from './kit/time';
import { errorMessage } from './errors';

// Ticket 188 D13: the Audit History tab. Staff see what changed on the contact.
// Workspace admins also see who touched protected material; the server decides
// (`sensitive_included`) and never sends a protected value, ip or user agent.
const PAGE = 100;

export default function ContactAudit({ contactId }) {
  const [state, setState] = useState({
    loading: true, error: null, events: [], nextBefore: null, sensitive: false,
  });
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [olderError, setOlderError] = useState('');
  const request = useRef(0);

  useEffect(() => {
    const mine = ++request.current;
    uc.audit(contactId, { limit: PAGE })
      .then((d) => {
        if (request.current !== mine) return;
        setState({
          loading: false, error: null, events: d.events || [],
          nextBefore: d.next_before || null, sensitive: !!d.sensitive_included,
        });
      })
      .catch((e) => {
        if (request.current !== mine) return;
        setState({ loading: false, error: errorMessage(e), events: [], nextBefore: null, sensitive: false });
      });
    return () => { request.current += 1; };
  }, [contactId]);

  const loadOlder = async () => {
    const mine = request.current;
    setLoadingOlder(true);
    setOlderError('');
    try {
      const d = await uc.audit(contactId, { limit: PAGE, before: state.nextBefore });
      if (request.current !== mine) return;
      setState((s) => ({ ...s, events: [...s.events, ...(d.events || [])], nextBefore: d.next_before || null }));
    } catch (e) {
      if (request.current === mine) setOlderError(errorMessage(e));
    } finally {
      setLoadingOlder(false);
    }
  };

  const { loading, error, events, nextBefore, sensitive } = state;
  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center text-slate-400" data-testid="audit-loading">
        <Loader2 className="animate-spin mr-2" size={18} /> Loading audit history...
      </div>
    );
  }
  if (error) {
    return (
      <div className="bg-red-50 border border-red-100 rounded-2xl p-8 text-center text-red-600 text-sm" data-testid="audit-error">
        {error}
      </div>
    );
  }
  return (
    <div className="space-y-4" data-testid="contact-audit">
      {events.length === 0 ? (
        <div className="bg-slate-50 border border-dashed border-slate-200 rounded-2xl p-16 text-center text-slate-400" data-testid="audit-empty">
          <p>No audit history yet</p>
        </div>
      ) : (
        <ul className="space-y-3" data-testid="audit-list">
          {events.map((e, i) => {
            const Icon = e.sensitive ? Lock : History;
            const fields = e.action === 'contact.update' ? (e.meta?.fields || []) : [];
            return (
              <li key={`${e.ts}-${i}`} data-testid={`audit-event-${e.action}`}
                className="flex gap-3 items-start bg-white border border-slate-100 rounded-xl p-4">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${e.sensitive ? 'bg-amber-50 text-amber-600' : 'bg-slate-100 text-slate-500'}`}>
                  <Icon size={14} />
                </div>
                <div className="flex-1">
                  <div className="text-sm text-slate-900 flex items-center gap-2 flex-wrap">
                    {e.title}
                    {e.sensitive && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700" data-testid="audit-sensitive-badge">
                        Sensitive access
                      </span>
                    )}
                  </div>
                  {fields.length > 0 && (
                    <div className="text-[11px] text-slate-500 mt-0.5" data-testid="audit-fields">
                      {fields.join(', ')}
                    </div>
                  )}
                  <div className="text-[11px] text-slate-400 mt-0.5">
                    {formatDateTime(e.ts)} · {e.actor_email || 'System'}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
      {nextBefore && (
        <div className="text-center">
          <button type="button" onClick={loadOlder} disabled={loadingOlder} data-testid="audit-load-older"
            className="text-xs px-3 py-1 rounded-full border border-slate-200 bg-white hover:bg-slate-50 disabled:opacity-60">
            {loadingOlder ? 'Loading...' : 'Load older'}
          </button>
          {olderError && <p className="text-xs text-red-600 mt-1" role="alert">{olderError}</p>}
        </div>
      )}
      {!sensitive && (
        <p className="text-xs text-slate-400" data-testid="audit-staff-note">
          SSN reveals, downloads and deletions are shown to admins only.
        </p>
      )}
    </div>
  );
}
